import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { MessageProcessor, type A2uiClientAction, type ComponentApi, type SurfaceModel } from '@a2ui/web_core/v0_9';
import '@a2ui/lit/v0_9'; // registers <a2ui-surface>
import { api, HttpError, isRetryable } from '../api';
import { recordSnippet } from '../pronounce';
import { onSpeaking, speak } from '../tts';
import { packWait, playButton, playState, pronWait, waitDots } from './wait-bits';
import { skeletonWords } from './wait-steps';
import { CATALOG_ID, SURFACE_ID, exerciseCatalog } from '../a2ui/catalog';
import { adoptAppStyles } from '../a2ui/adopt-styles';
import { routeOutcome, startRouteTask } from '../research-client';
import type { ResearchAttempt } from '../research/delivery';
import type { RouteTask } from '../../../server/src/research/schema.js';
import { go, setLeaveGuard } from '../router';
import type { Allowance, AttemptInput, ItemStatus, Moment, MomentProgress, PackTask, SkipReason } from '../types';
import { cardMs, cardShown } from '../card-time';
import { aiOf, aiOut, aiReason } from '../ai-allowance';
import { answerHidden, COMPONENT, givenText, listenAudioText, noteMiss as noteMissIn, showAnswerOn, skipReasonsFor, taskLabel, withoutSaying, type MissNote } from './lesson-view';
import { errorText, t } from '../i18n';
import { backGlyph, iso, targetList, tPart, ui, uiDir } from '../bidi-html';
import { missedRow } from './missed-row';
import { makeClientId } from '../drafts';
import { currentOwner, resolveSession, session } from '../session';
import { armFeel, feel } from '../feel';
import './end-note';
import { emptyTally, missedWords, summaryOf, tallyAnswer, type Tally } from '../town/end-summary';
import { normalise } from '../a2ui/base';
import { revealBar } from './reveal-bar';

/** An answer the server has not confirmed; its id makes a retry count once. */
type UnsavedAttempt = { a: AttemptInput & { attemptId: string }; label: string | null };
type RemovedPracticeTask = {
  itemKey: string;
  tasks: Array<{ task: PackTask; index: number; misses: MissNote[] }>;
  answered: boolean;
  showMe: boolean;
  previousStatus: ItemStatus;
  statusVersion: number;
  undoOperationId?: string;
};
/** Saved; retryable; a generic refusal; or a removed-item refusal with its own Undo guidance. */
type SendResult = 'saved' | 'retry' | 'refused' | 'item-removed';
const ANSWER_TIMEOUT_MS = 8_000;
const PROGRESS_TIMEOUT_MS = 5_000;
const FSI = '⁨', PDI = '⁩';

function isItemStatusConflict(error: unknown): boolean {
  return error instanceof HttpError && error.status === 409 && error.code === 'item-status-conflict';
}

/** A hung request becomes retryable; the stable attempt id makes a late server reply safe to retry. */
function bounded<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

/**
 * True only the first time this account opens a lesson on this device: that
 * lesson says once what Test records and what Study is for (ticket v1-02).
 */
async function firstTestIntro(): Promise<boolean> {
  // A lesson opened straight from a link can load before the session has
  // resolved: wait for the account, so the flag is always that account's.
  if (session().status === 'unknown') await resolveSession();
  const owner = currentOwner();
  if (!owner) return false;
  const key = `li-test-intro:${owner}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, '1');
    return true;
  } catch {
    return false; // private mode: the short hint still explains Test
  }
}

/** Why today's lesson brings fewer new words (v1 ticket 30): the words waiting for review now. */
function fewerLine(waiting: number): string {
  if (waiting <= 0) return t('fog.fewerNone');
  return waiting === 1 ? t('fog.fewerOne') : t('fog.fewer', { n: waiting });
}

/**
 * Deterministic practice: one cached pack task at a time, advancing only on
 * the learner's own trigger. Study shows the cards freely (nothing recorded);
 * Test hard-gates — Next unlocks only after the card's answer, Skip records
 * a skipped attempt, and a scored miss brings that word's easier card next
 * (ticket 04). No model calls while practising.
 */
@customElement('practice-view')
export class PracticeView extends LitElement {
  @property() momentId = '';
  /** Where this practice was opened from, checked by the router and kept through sign-in. */
  @property() returnTo = '';
  @state() private moment?: Moment;
  @state() private tasks: PackTask[] = [];
  /** Exposure IDs for which this browser has already reported entering Study mode. */
  private studyLogged = new Set<string>();
  /** Easier cards served after a miss (ticket 04), asked next; not part of the pack, so "Again" drops them. */
  private retreatTasks = new Set<PackTask>();
  @state() private packCached = false;
  /** Set when waiting reviews cut today's lesson (v1 ticket 30): how many words wait. */
  @state() private fewer?: { waiting: number };
  @state() private mode: 'study' | 'test' = 'test';
  /** Answers on their way to the server: the end's promise waits for them (ticket 37). */
  private inflight = new Set<Promise<unknown>>();
  /** Lets the learner's taps wake the effects' audio while the lesson is on screen (ticket 37). */
  private unarmFeel: (() => void) | null = null;
  /** First lesson on this device: explain Test and Study once. */
  @state() private testIntro = false;
  @state() private i = 0;
  @state() private answered = false;
  /** Ticket 32: a miss left the answer off screen, so the bar offers Show me instead of Next. */
  @state() private showMe = false;
  /** Task identity survives queue splices, so undoing one card cannot unmark another answer. */
  private recordedTasks = new Set<string>();
  /** Words this run took up to box 3 (forged, v1 ticket 10). */
  @state() private forgedNow: string[] = [];
  /** This run's saved answers, for the end's headline and score (test mode). */
  @state() private tally: Tally = emptyTally();
  @state() private misses: MissNote[] = [];
  @state() private done = false;
  /** The learner chose to stop before reaching the last card. */
  @state() private endedEarly = false;
  @state() private ending = false;
  /**
   * Bumped when Undo restores cards, so a completion still in flight
   * (finishCompleted/finishEarly) stands down instead of finishing over the
   * restored cards (ticket 92). Undo from the end screen after a completion
   * settled needs nothing: `ending` is already false, so nothing is in flight.
   */
  private finishSeq = 0;
  @state() private progress?: MomentProgress;
  @state() private progressUnavailable = false;
  @state() private loading = true;
  @state() private error = '';
  @state() private saving = false;
  @state() private pronPhase: 'idle' | 'recording' | 'scoring' = 'idle';
  @state() private pronTip = '';
  @state() private pronLevel = 0;
  @state() private pronError = '';
  private pronStop = { stop: false };
  @state() private skipMenu = false;
  /** Most recent removals stay undoable in reverse order for this open session. */
  @state() private removedTasks: RemovedPracticeTask[] = [];
  /** Reuse the same removal operation id if the server committed but its response was lost. */
  private pendingRemovalOperations = new Map<string, string>();
  @state() private actionError = '';
  @state() private speaking: string | null = null;
  /** Whether `speaking` is heard yet, or its voice still on its way (the play button prepares meanwhile). */
  @state() private heard = false;
  /** The moment's own words, shown faintly on the skeleton cards while the first pack is written. */
  @state() private waitWords: string[] = [];
  private unsubSpeaking: (() => void) | null = null;
  private autoTimer: ReturnType<typeof setTimeout> | undefined;
  @state() private surface?: SurfaceModel<never>;
  /** Beta: this month's AI credit, so paid controls can say why they are off. */
  @state() private allowance?: Allowance;
  @state() private refreshing = false;
  @state() private refreshError = '';
  /** Answers whose save failed: kept for Retry, across cards, until they land. */
  @state() private unsaved: UnsavedAttempt[] = [];
  @state() private retrying = false;
  /** Answers the server refused outright: Retry can't help, so they are only counted until dismissed. */
  @state() private lost = 0;
  @state() private attemptError = '';
  /** Per-sitting counts change only after the server acknowledges the attempt. */
  private studySeenTasks = new Set<string>();
  private testViewedTasks = new Set<string>();
  /** Tasks touched in Test mode, distinct from cards merely displayed in Test. */
  private testSeenTasks = new Set<string>();
  /** Distinct task ids with any server-acknowledged attempt, including a saved skip or self-report. */
  private savedRunTasks = new Set<string>();
  private revealedRunTasks = new Set<string>();
  private hintedRunTasks = new Set<string>();
  private skippedRunTasks = new Set<string>();
  private selfReportedRunTasks = new Set<string>();
  private sendOperations = new Set<Promise<SendResult>>();
  /** Leaving (in the app, Back, closing the tab) with unsaved answers asks first: they live only here. */
  private leaveGuard = () => (this.unsaved.length || this.sendOperations.size ? t('lesson.leaveUnsaved') : null);

  private processor: MessageProcessor<ComponentApi> | undefined;
  private actionHandler = (a: A2uiClientAction) => this.onAction(a);

  protected createRenderRoot() { return this; }

  async connectedCallback() {
    super.connectedCallback();
    setLeaveGuard(this.leaveGuard);
    this.routeTask = this.returnTo === '/#moments' ? 'practice_repeat' : 'practice_direct';
    this.routeAttempt = startRouteTask(this.routeTask, this.returnTo === '/#moments' ? 'home' : 'moment');
    this.routeShown = false;
    this.unarmFeel = armFeel();
    this.processor = new MessageProcessor([exerciseCatalog], this.actionHandler);
    this.unsubSpeaking = onSpeaking((now, heard) => {
      this.speaking = now;
      this.heard = heard;
    });
    this.processor.onSurfaceCreated((s) => {
      if (s.id === SURFACE_ID) this.surface = s as unknown as SurfaceModel<never>;
    });
    this.processor.onSurfaceDeleted((id) => {
      if (id === SURFACE_ID) this.surface = undefined;
    });
    try {
      // The allowance is advisory here: practice itself is free, and the
      // server refuses anything paid on its own. A failure must not stop the
      // lesson, so it is read separately.
      void api.allowance().then((a) => (this.allowance = a)).catch(() => undefined);
      const moment = api.getMoment(this.momentId);
      // Deliberately fatal: without the triage statuses we cannot know the
      // removed set, and silently proceeding would resurrect cards the
      // learner removed as wrong. Same local DB behind all three calls, so
      // an isolated failure here is near-impossible — fail loudly instead.
      const itemsNow = api.momentItems(this.momentId);
      // The title and the moment's own words are drawn while the pack is written (a first open waits on the model);
      // a failure of either is reported by the wait below.
      moment.then((m) => { this.moment ??= m; }, () => undefined);
      itemsNow.then((items) => {
        this.waitWords = skeletonWords(items.items
          .filter((it) => it.kind !== 'line' && it.status !== 'removed' && !it.opens)
          .map((it) => it.latin || it.ca));
      }, () => undefined);
      const [m, p, items] = await Promise.all([moment, api.pack(this.momentId), itemsNow]);
      this.moment = m;
      // Cards the learner removed as wrong never come back — not even in the
      // home moment's own pack (the cached pack still lists them).
      const removed = new Set(items.items.filter((it) => it.status === 'removed').map((it) => it.key));
      this.tasks = removed.size ? p.pack.tasks.filter((task) => !removed.has(task.itemKey)) : p.pack.tasks;
      this.packCached = p.cached;
      this.fewer = p.fewer;
      if (!this.tasks.length) this.error = t('practice.noTasks');
      else {
        this.renderTask();
        void firstTestIntro().then((show) => (this.testIntro = show));
      }
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.loading = false;
    }
  }

  /** The content-free route attempt for this visit (ticket 55): started on entry, shown with the first card. */
  private routeAttempt: ResearchAttempt | null = null;
  private routeTask: RouteTask = 'practice_direct';
  private routeShown = false;

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.routeShown) routeOutcome(this.routeAttempt, this.routeTask, 'wrong_turn');
    this.routeAttempt = null;
    setLeaveGuard(null);
    this.unarmFeel?.();
    this.unarmFeel = null;
    this.unsubSpeaking?.();
    this.unsubSpeaking = null;
    this.clearAutoAdvance();
    this.processor?.model.dispose();
    this.processor = undefined;
  }

  protected updated(changed: PropertyValues) {
    if (changed.has('answered') && this.answered) revealBar(this);
    adoptAppStyles(this.querySelector('a2ui-surface')?.shadowRoot);
    const task = this.tasks[this.i];
    if (task && !this.loading && !this.done) {
      cardShown(this.momentId, task.id);
      if (!this.routeShown) { this.routeShown = true; routeOutcome(this.routeAttempt, this.routeTask, 'displayed'); }
      if (this.mode === 'study' && task.exposureId && !this.studyLogged.has(task.exposureId)) {
        this.studyLogged.add(task.exposureId);
        this.exerciseEvent(task, 'study');
      }
    }
  }

  private exerciseEvent(task: PackTask, kind: 'hint' | 'retry' | 'reveal' | 'study' | 'browser_ack') {
    if (task.exposureId) void api.exerciseEvent(this.momentId, task.exposureId, makeClientId(), kind).catch(() => undefined);
  }

  private exitLabel(): string {
    return this.returnTo === '/#moments' ? t('practice.returnMoments') : t('practice.moment');
  }

  /** A list entry returns to the list; a direct lesson link keeps the established moment-details exit. */
  private exitPractice = () => go(this.returnTo || `/moment/${this.momentId}`, true);

  render() {
    const m = this.moment;
    const audio = this.audioText();
    return html`
      <div class="screen">
        <div class="topbar">
          <button class="btn ghost practice-exit" @click=${this.exitPractice} aria-label=${this.exitLabel()}>${backGlyph()} ${this.exitLabel()}</button>
          <h1 style="font-size:17px" dir="auto">${m?.analysis.title ?? t('practice.title')}</h1>
          ${this.packCached || this.loading ? nothing : html`<span class="pill" title=${t('practice.newPillTitle')}>${t('practice.newPill')}</span>`}
        </div>

        ${this.loading
          ? this.renderWait()
          : this.error
            ? html`<div class="card"><strong>${t('practice.loadError')}</strong><div class="muted small">${this.error}</div></div>`
            : this.done
              ? this.renderDone()
              : html`
                <div class="muted small practice-frame" style="margin-bottom:8px">${t('practice.frame')}</div>
                <div style="display:flex;gap:8px;margin-bottom:10px">
                  <button class="btn ${this.mode === 'study' ? 'primary' : ''}" style="flex:1" @click=${() => this.setMode('study')} ?disabled=${this.ending}>${t('practice.study')}</button>
                  <button class="btn ${this.mode === 'test' ? 'primary' : ''}" style="flex:1" @click=${() => this.setMode('test')} ?disabled=${this.ending}>${t('practice.test')}</button>
                </div>
                ${this.testIntro ? html`<div class="note test-intro" style="margin-bottom:8px">${t('practice.testIntro')}</div>` : nothing}
                ${this.mode === 'study'
                  ? html`<div class="muted small" style="margin-bottom:8px">${t('practice.studyHint')}</div>`
                  : html`<div class="muted small test-hint" style="margin-bottom:8px">${t('practice.testHint')}</div>`}
                ${this.fewer ? html`<div class="muted small fewer-new" style="margin-bottom:8px">${fewerLine(this.fewer.waiting)}</div>` : nothing}
                ${this.isRetreat()
                  ? html`<p class="card-name" style="margin:0 0 4px;font-weight:600">${t('retreat.card')}</p>
                      <p class="muted small retreat-hint" style="margin:0 0 8px">${t('retreat.hint')}</p>`
                  : nothing}
                ${audio
                  ? html`<div class="card" style="display:flex;gap:10px;align-items:center;margin-bottom:8px">
                      ${playButton(playState(this.speaking, this.heard, audio), this.playAudio)}
                      <span class="muted small">${t('practice.listenHint')}</span>
                    </div>`
                  : nothing}
                <div class="surface">
                  ${this.surface
                    ? html`<a2ui-surface ?giveup=${this.mode === 'test'} .surface=${this.surface}></a2ui-surface>`
                    : html`<div class="surface-empty"><span class="muted">${t('lesson.loadingCard')}</span></div>`}
                </div>
                <div class="actionbar column ${this.mode === 'test' && !this.answered && !this.showMe && this.currentTask()?.type !== 'sayitback' ? 'waiting' : ''}">
                ${this.currentTask()?.type === 'sayitback' && this.mode === 'test' && !this.moment?.mock
                  ? aiOut(this.allowance)
                  ? html`<div class="muted small pron-unavailable ai-reason">
                      ${t('ai.pronReason', { reason: aiReason(aiOf(this.allowance)!) })}
                    </div>`
                  : html`<div>
                      <button class="btn block ${this.pronPhase === 'scoring' ? 'preparing' : ''}" @click=${this.togglePron}
                        ?disabled=${this.pronPhase === 'scoring' || this.saving || this.ending}>
                        ${this.pronPhase === 'recording' ? t('lesson.stopCheck') : this.pronPhase === 'scoring' ? t('lesson.scoring') : t('lesson.sayCheck')}
                      </button>
                      ${this.pronPhase === 'scoring' ? pronWait(String(this.currentTask()?.props.ca ?? '')) : nothing}
                      ${this.pronPhase === 'recording'
                        ? html`<div class="level" style="margin-top:6px"><div style="width:${Math.round(this.pronLevel * 100)}%"></div></div>`
                        : nothing}
                      ${this.pronTip ? html`<div class="note" dir="auto" style="margin-top:6px">${this.pronTip}</div>` : nothing}
                      ${this.pronError ? html`<div class="muted small" style="margin-top:6px">${t('lesson.pronErrorTail', { err: this.pronError })}</div>` : nothing}
                    </div>`
                  : nothing}
                <div style="display:flex;gap:8px">
                  ${this.mode === 'test' && !this.showMe
                    ? html`<button class="btn" style="flex:0 0 auto" @click=${this.toggleSkipMenu} ?disabled=${this.ending}>${t('lesson.skip')}</button>`
                    : nothing}
                  ${this.mode === 'test' ? html`<button class="btn danger" style="flex:0 0 auto" @click=${this.removeCurrentCard} ?disabled=${this.saving}>${t('lesson.removeAction')}</button>` : nothing}
                  ${this.showMe
                    ? html`<button class="btn primary block" style="flex:1" @click=${this.showAnswer} ?disabled=${this.saving || this.ending}>${t('ex.showMe')}</button>`
                    : html`<button class="btn primary block" style="flex:1" @click=${this.next}
                        ?disabled=${this.mode === 'test' && !this.answered || this.saving || this.ending}>
                        ${this.i + 1 >= this.tasks.length ? t('lesson.finish') : t('lesson.next')}
                      </button>`}
                </div>
                ${this.actionError
                  ? html`<div class="muted small" role="alert" style="margin-top:6px">${this.actionError}</div>`
                  : nothing}
                ${this.renderUnsaved()}
                ${this.mode === 'test' && this.skipMenu && !this.showMe
                  ? html`<div class="card" style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap">
                      <span class="muted small" style="width:100%">${t('lesson.whySkip')}</span>
                      ${skipReasonsFor(this.tasks[this.i]?.type ?? '').map((r) => html`<button class="btn" @click=${() => this.skipWithReason(r)}>${t(`skip.${r}`)}</button>`)}
                    </div>`
                  : nothing}
                </div>
                <div style="display:flex;justify-content:flex-end;margin-top:8px">
                  <button class="btn ghost practice-stop-early" @click=${this.finishEarly}
                    ?disabled=${this.ending || this.saving && this.sendOperations.size === 0} aria-busy=${this.ending ? 'true' : 'false'}>
                    ${this.ending ? t('lesson.saving') : t('practice.stopEarly')}
                  </button>
                </div>
                ${this.forgedNow.length
                  ? html`<div class="muted small" style="margin-top:8px" dir=${ui()}>${tPart('practice.forgedNow', 'list', targetList(this.forgedNow))}</div>`
                  : nothing}
              `}
        ${this.removedTasks.length ? html`<button class="btn" style="margin-top:8px" @click=${this.undoRemoval} ?disabled=${this.saving}>${t('lesson.undoRemoval')}</button>` : nothing}
        ${this.done && this.actionError ? html`<div class="error small" role="alert" style="margin-top:8px">${this.actionError}</div>` : nothing}
      </div>
    `;
  }

  /**
   * While the pack loads (a first open waits on the model): what is being made, then skeleton cards with the
   * moment's own words on them. Shown only after a moment, so a cached pack never flashes it.
   */
  private renderWait() {
    return packWait(this.waitWords, 'practice-wait');
  }

  private renderDone() {
    const p = this.progress;
    const m = this.moment;
    const mission = m?.mission ?? m?.analysis.mission ?? '';
    const visited = new Set([...this.studySeenTasks, ...this.testViewedTasks]).size;
    const mixed = this.studySeenTasks.size > 0 && this.testViewedTasks.size > 0;
    return html`
      <div class="card" style="text-align:center">
        <div class=${this.refreshing ? 'wait-dim' : ''}>
        <div style="font-size:32px">✓</div>
        <div class="practice-done-title"><strong>${t(this.endedEarly ? 'practice.endedEarly' : 'practice.doneTitle')}</strong>
          ${this.endedEarly
            ? html`<span class="practice-ended-early">${t('practice.endedEarlySub', { visited, total: this.tasks.length })}</span>`
            : mixed
              ? t('practice.doneMixedSub', { n: this.tasks.length })
              : this.mode === 'study'
                ? nothing
                : t('practice.doneSub', { n: this.tasks.length, mode: t('practice.test') })}
        </div>
        ${this.testSeenTasks.size > 0 || this.savedRunTasks.size > 0 || this.skippedRunTasks.size > 0 || this.selfReportedRunTasks.size > 0
          ? html`<div class="muted small practice-session-progress" style="margin-top:8px">
              ${t('practice.sessionSaved', {
                saved: this.savedRunTasks.size,
                total: this.tasks.length,
                revealed: this.revealedRunTasks.size,
                hinted: this.hintedRunTasks.size,
                skipped: this.skippedRunTasks.size,
                selfReported: this.selfReportedRunTasks.size,
              })}
            </div>`
          : nothing}
        ${this.studySeenTasks.size > 0
          ? html`<div class="muted small practice-session-study" style="margin-top:8px">
              ${t('practice.sessionStudy', { visited: this.studySeenTasks.size, total: this.tasks.length })}
            </div>`
          : nothing}
        ${p
          ? this.progressUnavailable
            ? html`<div class="muted small practice-progress-unavailable" style="margin-top:8px">${t('practice.progressUnavailable')}</div>`
            : html`<div class="muted small practice-saved-progress" style="margin-top:8px">${t('practice.progress', { saved: p.savedActivityTasks, tested: p.testedTasks, forged: p.forgedHere })}</div>`
          : this.progressUnavailable
            ? html`<div class="muted small practice-progress-unavailable" style="margin-top:8px">${t('practice.progressUnavailable')}</div>`
            : html`<div class="muted small" style="margin-top:8px">${t('practice.loadingProgress')}</div>`}
        ${this.forgedNow.length
          ? html`<div class="small" style="margin-top:8px" dir=${ui()}>${tPart('practice.forgedRun', 'list', targetList(this.forgedNow))}</div>`
          : nothing}
        ${this.testSeenTasks.size > 0
          ? html`<end-note style="display:block;margin-top:12px" .landed=${Promise.allSettled([...this.inflight])} .summary=${summaryOf(this.tally)} .missed=${missedWords(this.tally)}></end-note>`
          : nothing}
        ${this.misses.length && this.testSeenTasks.size === 0
          ? html`<div class="card" style="margin-top:10px;text-align:start">
              <div><strong>${t('lesson.worthLook')}</strong> <span class="muted small">${t('lesson.worthLookHint')}</span></div>
              ${this.misses.map(missedRow)}
            </div>`
          : nothing}
        ${mission
          ? html`<div class="card" style="margin-top:10px;text-align:start">
              <div><strong>${t('moment.mission')}</strong></div>
              <div class="small" style="margin-top:4px" dir=${uiDir(mission)}>${iso(mission)}</div>
              <button class="btn small" style="margin-top:8px;text-transform:none" @click=${() => go(`/moment/${this.momentId}`)}>${t('practice.reflect')}</button>
            </div>`
          : nothing}
        <lesson-feedback scope=${`moment:${this.momentId}`}></lesson-feedback>
        <div style="display:flex;gap:8px;margin-top:14px">
          <button class="btn" style="flex:1" @click=${this.exitPractice}>${this.exitLabel()}</button>
          <button class="btn primary practice-again" style="flex:1" @click=${this.restart}>${t('practice.again')}</button>
        </div>
        </div>
        <button class="btn ghost block pack-refresh ${this.refreshing ? 'preparing' : ''}" style="margin-top:8px"
          ?disabled=${this.refreshing || aiOut(this.allowance)} aria-busy=${this.refreshing ? 'true' : 'false'} @click=${this.refreshPack}>
          ${this.refreshing ? waitDots(t('wait.newPack')) : t('ai.refreshPack')}
        </button>
        ${this.refreshing ? html`<wait-caption .lines=${[t('wait.newPack'), t('wait.practice.lines')]}></wait-caption>` : nothing}
        ${aiOut(this.allowance)
          ? html`<div class="muted small ai-reason" style="margin-top:4px">
              ${t('ai.refreshReason', { reason: aiReason(aiOf(this.allowance)!) })}
            </div>`
          : nothing}
        ${this.refreshError ? html`<div class="muted small" style="margin-top:4px">${this.refreshError}</div>` : nothing}
        ${this.renderUnsaved()}
      </div>
    `;
  }

  /** Non-blocking: the learner keeps going; Retry sends the same answers again, a refusal is only dismissed. */
  private renderUnsaved() {
    const n = this.unsaved.length;
    const lost = this.lost;
    const attemptError = this.attemptError;
    if (!n && !lost && !attemptError) return nothing;
    return html`
      ${n
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${n === 1 ? t('lesson.saveFailed') : t('lesson.saveFailedMany', { n })}</span>
            <button class="btn small" @click=${this.retryUnsaved} ?disabled=${this.retrying || this.saving || this.ending}>
              ${this.retrying ? t('lesson.saving') : t('common.retry')}
            </button>
          </div>`
        : nothing}
      ${lost
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${lost === 1 ? t('lesson.saveRefused') : t('lesson.saveRefusedMany', { n: lost })}</span>
            <button class="btn small" @click=${() => (this.lost = 0)} ?disabled=${this.ending}>${t('common.dismiss')}</button>
          </div>`
        : nothing}
      ${attemptError
        ? html`<div class="note save-error" role="alert" style="margin-top:6px;display:flex;gap:8px;align-items:center">
            <span style="flex:1">${attemptError}</span>
            <button class="btn small" @click=${() => (this.attemptError = '')} ?disabled=${this.ending}>${t('common.dismiss')}</button>
          </div>`
        : nothing}
    `;
  }

  private setMode(mode: 'study' | 'test') {
    if (this.mode === mode || this.ending || this.done) return;
    this.mode = mode;
    // Re-render the current card fresh so study/test state never leaks across modes.
    this.clearAutoAdvance();
    this.answered = false;
    this.showMe = false;
    this.resetPron();
    this.renderTask();
  }

  private currentTask() {
    return this.tasks[this.i];
  }

  private isRetreat(): boolean {
    const task = this.currentTask();
    return this.mode === 'test' && !!task && this.retreatTasks.has(task);
  }

  /**
   * The easier card after a miss (ticket 04), asked next in test mode. The
   * server keeps it waiting until it is answered, so one left over leads the
   * next review.
   */
  private queueRetreat(task: PackTask) {
    if (this.done || this.mode !== 'test' || !this.tasks.length) return;
    if (this.tasks.some((x, k) => k > this.i && this.retreatTasks.has(x) && x.itemKey === task.itemKey)) return;
    this.retreatTasks.add(task);
    const at = this.i + 1;
    this.tasks = [...this.tasks.slice(0, at), task, ...this.tasks.slice(at)];
  }

  /** Catalan line to play aloud for listen tasks (hearing only — never shown as text). */
  private audioText(): string | null {
    return listenAudioText(this.currentTask()?.props as Record<string, unknown> | undefined);
  }

  private playAudio = () => {
    const a = this.audioText();
    if (a) void speak(this.momentId, a, { mock: this.moment?.mock, lang: this.moment?.targetLanguage });
  };

  private resetPron() {
    this.pronStop.stop = true;
    this.pronPhase = 'idle';
    this.pronTip = '';
    this.pronError = '';
    this.pronLevel = 0;
  }

  /** Tap to record, tap again to stop; the score counts as this card's production attempt. */
  private togglePron = async () => {
    const task = this.currentTask();
    if (!task || task.type !== 'sayitback') return;
    if (this.pronPhase === 'recording') {
      this.pronStop.stop = true;
      return;
    }
    if (this.pronPhase !== 'idle') return;
    this.testSeenTasks.add(task.id);
    this.pronPhase = 'recording';
    this.pronTip = '';
    this.pronError = '';
    this.pronStop.stop = false;
    try {
      const blob = await recordSnippet((l) => (this.pronLevel = l), this.pronStop);
      if (this.ending || this.done) {
        this.pronPhase = 'idle';
        return;
      }
      this.pronPhase = 'scoring';
      const expected = String(task.props.ca ?? '');
      const r = await api.scorePronunciation(this.momentId, blob, expected);
      if (this.ending || this.done) {
        this.pronPhase = 'idle';
        return;
      }
      this.pronPhase = 'idle';
      this.pronTip = r.pass ? t('lesson.pronPass') : t('lesson.pronFail', { tip: r.tip });
      // First scoring counts as assessed pronunciation; a fail leaves Next locked (retry, skip, or "I said it").
      if (!this.recordedTasks.has(task.id)) {
        this.recordedTasks.add(task.id);
        await this.record({
          taskId: task.id,
          itemKey: task.itemKey,
          type: task.type,
          correct: r.pass,
          production: true,
          productionKind: 'pronunciation',
          assessed: true,
        });
      }
      if (r.pass) {
        this.answered = true;
        this.scheduleAutoAdvance(900);
      }
    } catch (e) {
      this.pronPhase = 'idle';
      this.pronError = errorText(e) || t('lesson.scoringFailed');
      // Out of AI credit the button disappears entirely next render, and the
      // card's own "I said it" carries the learner through.
      void api.allowance().then((a) => (this.allowance = a)).catch(() => undefined);
    }
  };

  /**
   * Buy a fresh pack for this moment and start it. Paid, so the server may
   * refuse it (402): re-read the allowance and let the control explain itself
   * rather than failing silently. The old pack stays until a new one arrives.
   */
  private refreshPack = async () => {
    if (this.refreshing) return;
    this.refreshing = true;
    this.refreshError = '';
    try {
      const p = await api.refreshPack(this.momentId);
      this.tasks = p.pack.tasks;
      this.packCached = false;
      this.fewer = p.fewer;
      this.restart();
    } catch (e) {
      this.refreshError = errorText(e);
      void api.allowance().then((a) => (this.allowance = a)).catch(() => undefined);
    } finally {
      this.refreshing = false;
    }
  };

  private restart = () => {
    this.clearAutoAdvance();
    // Undo snapshots belong to this run. Reusing them after Again could restore
    // stale miss notes or recorded state into a newly answered card.
    this.removedTasks = [];
    if (this.retreatTasks.size) {
      this.tasks = this.tasks.filter((task) => !this.retreatTasks.has(task));
      this.retreatTasks = new Set();
    }
    this.i = 0;
    this.recordedTasks.clear();
    this.done = false;
    this.endedEarly = false;
    this.ending = false;
    this.answered = false;
    this.showMe = false;
    this.misses = [];
    this.tally = emptyTally();
    this.forgedNow = [];
    this.studySeenTasks = new Set();
    this.testViewedTasks = new Set();
    this.testSeenTasks = new Set();
    this.savedRunTasks = new Set();
    this.revealedRunTasks = new Set();
    this.hintedRunTasks = new Set();
    this.skippedRunTasks = new Set();
    this.selfReportedRunTasks = new Set();
    this.actionError = '';
    this.resetPron();
    this.renderTask();
    void this.refreshProgress();
  };

  /** End this sitting without describing an incomplete pack as complete. */
  private finishEarly = async () => {
    if (this.done || this.ending || this.loading || this.error) return;
    this.clearAutoAdvance();
    this.resetPron();
    this.ending = true;
    const mine = this.finishSeq;
    // Wait for every in-flight response and local tally update before reading
    // the authoritative cumulative progress shown on the end screen. Undo
    // during the waits restores cards: stand down like finishCompleted does.
    await this.waitForSends();
    if (mine !== this.finishSeq) { this.ending = false; return; }
    await this.refreshProgress();
    if (mine !== this.finishSeq) { this.ending = false; return; }
    if (!this.isConnected) return;
    this.endedEarly = true;
    this.done = true;
    this.ending = false;
  };

  /** Recreate the surface on every card so exercise state starts fresh. */
  private resetSurface() {
    this.processor?.processMessages([{ version: 'v0.9', deleteSurface: { surfaceId: SURFACE_ID } }]);
    this.processor?.processMessages([{ version: 'v0.9', createSurface: { surfaceId: SURFACE_ID, catalogId: CATALOG_ID } }]);
  }

  /** Flashcards grade in one tap — auto-advance so the learner never needs a second Next tap. */
  private scheduleAutoAdvance(delay = 650) {
    this.clearAutoAdvance();
    const i = this.i;
    this.autoTimer = setTimeout(() => {
      if (this.done || this.i !== i) return;
      this.advance();
    }, delay);
  }

  private clearAutoAdvance() {
    if (this.autoTimer !== undefined) clearTimeout(this.autoTimer);
    this.autoTimer = undefined;
  }

  private renderTask() {
    const task = this.tasks[this.i];
    if (!task || !this.processor) return;
    (this.mode === 'study' ? this.studySeenTasks : this.testViewedTasks).add(task.id);
    this.resetSurface();
    try {
      this.processor.processMessages([{
        version: 'v0.9',
        updateComponents: {
          surfaceId: SURFACE_ID,
          components: [
            { id: 'root', component: 'Column', children: ['p', 'q'] },
            { id: 'p', component: 'Progress', done: this.i + 1, total: this.tasks.length },
            { id: 'q', component: COMPONENT[task.type], ...task.props },
          ],
        },
      }] as never);
    } catch (e) {
      console.error('practice render failed', e);
      this.error = t('lesson.cardError', { n: this.i + 1 });
    }
  }

  /** Remember a miss for the recap; a later correct retry on the same card clears it. */
  private noteMiss(task: PackTask, ctx: Record<string, unknown>, correct: boolean) {
    this.misses = noteMissIn(this.misses, this.i, task.props as Record<string, unknown>, task.itemKey, ctx, correct);
  }

  /** First interaction with the card unlocks Next; in test mode it also records the attempt. */
  private onAction(action: A2uiClientAction) {
    if (this.done || this.ending) return;
    const task = this.tasks[this.i];
    if (!task) return;
    // Hint reveals are rescue tiers: free, scoring-neutral, never unlock Next.
    if (action.name === 'hint') {
      if (this.mode === 'test') this.testSeenTasks.add(task.id);
      this.exerciseEvent(task, 'hint');
      return;
    }
    const ctx = (action.context ?? {}) as Record<string, unknown>;
    const hintsUsed = typeof ctx.hintsUsed === 'number' ? ctx.hintsUsed : undefined;
    // "My answer should count" is a log-only flag for tuning — no scheduling effect.
    if (action.name === 'dispute') {
      if (this.mode !== 'test') return;
      this.testSeenTasks.add(task.id);
      const given = typeof ctx.value === 'string' ? ctx.value.slice(0, 200) : undefined;
      this.misses = this.misses.map((m) => (m.i === this.i ? { ...m, disputed: true, given: given ?? m.given } : m));
      void this.record({
        taskId: task.id, itemKey: task.itemKey, type: task.type, correct: false,
        skipped: true, skipReason: 'dispute', flagged: true, given, hintsUsed,
        production: task.production, productionKind: task.productionKind,
      });
      return;
    }
    // A flashcard flip only reveals — the verdict comes from the grade buttons.
    if (action.name === 'flipped') {
      if (this.mode === 'test') this.testSeenTasks.add(task.id);
      this.exerciseEvent(task, 'reveal');
      if (this.mode !== 'test') this.answered = true;
      return;
    }
    if (this.recordedTasks.has(task.id)) {
      // Already answered this card — still unlock Next (e.g. revealed after a wrong try).
      this.answered = true;
      const hidden = answerHidden(task.type, action.name, ctx);
      if (this.showMe && !hidden) this.answerSeen(task, ctx);
      else if (ctx.revealed === true) this.exerciseEvent(task, 'reveal');
      else if (action.name === 'answer' || action.name === 'grade') this.exerciseEvent(task, 'retry');
      // The card was drawn afresh (a switch to study and back) and missed again.
      else if (hidden && this.mode === 'test') this.showMe = true;
      return;
    }
    this.answered = true;
    if (this.mode !== 'test') return;
    this.testSeenTasks.add(task.id);
    this.recordedTasks.add(task.id);
    // "I said it" on a SayItBack card is self-reported practice, not assessed evidence.
    if (task.type === 'sayitback' && action.name === 'ready') {
      void this.record({
        taskId: task.id,
        itemKey: task.itemKey,
        type: task.type,
        correct: true,
        production: true,
        productionKind: 'self_report',
        selfReport: true,
      });
      this.scheduleAutoAdvance(400);
      return;
    }
    const correct = action.name === 'answer' || action.name === 'grade' ? ctx.correct === true : true;
    this.noteMiss(task, ctx, correct);
    // Ticket 32: never leave a miss without its answer on screen.
    this.showMe = answerHidden(task.type, action.name, ctx);
    if (this.showMe) this.skipMenu = false;
    void this.record({
      taskId: task.id,
      itemKey: task.itemKey,
      type: task.type,
      correct,
      revealed: ctx.revealed === true,
      production: task.production,
      productionKind: task.productionKind,
      // assessed is only for machine-scored pronunciation (togglePron below), never for typed/choice answers.
      hintsUsed,
      given: givenText(task.type, ctx, correct),
    });
    // A flashcard grade already is the verdict — no second Next tap needed.
    if (task.type === 'flashcard' && action.name === 'grade') this.scheduleAutoAdvance();
  }

  /**
   * The answer is on screen after a miss (ticket 32), so Next comes back. A
   * Show me is saved once, unscored, as a reveal; a right retry or a scratch
   * after the miss is not saved, as before.
   */
  private answerSeen(task: PackTask, ctx: Record<string, unknown>) {
    this.showMe = false;
    if (ctx.revealed !== true) return;
    this.exerciseEvent(task, 'reveal');
    void this.record({
      taskId: task.id, itemKey: task.itemKey, type: task.type, correct: false, revealed: true,
      exposureId: task.exposureId,
      production: task.production, productionKind: task.productionKind,
      hintsUsed: typeof ctx.hintsUsed === 'number' ? ctx.hintsUsed : undefined,
    });
  }

  /** The bar's Show me: the card shows its answer, and its reveal comes back through onAction. */
  private showAnswer = () => {
    if (!showAnswerOn(this)) this.showMe = false;
  };

  private async record(a: Omit<import('../types').AttemptInput, 'taskId' | 'itemKey' | 'type'> & { taskId: string; itemKey: string; type: string }) {
    await this.recordAt(this.i, a);
  }

  /** Save an attempt for the card at index `at` (skip advances first, then saves). */
  private async recordAt(at: number, a: Omit<import('../types').AttemptInput, 'taskId' | 'itemKey' | 'type'> & { taskId: string; itemKey: string; type: string }) {
    // Look the card up by task id, not by index: skip advances before its
    // save runs, so this.i already points at the next card. (Base code read
    // tasks[this.i]; the slice keeps that lookup for non-skip saves, where
    // the index has not moved.)
    const card = this.tasks.find((candidate) => candidate.id === a.taskId) ?? this.tasks[at] ?? this.tasks[this.i];
    const label = card ? taskLabel(card.props as Record<string, unknown>, card.itemKey) : null;
    // No step is sent: the server finds the card in this moment's pack and saves the step it served.
    this.saving = true;
    const exercise = this.tasks.find((candidate) => candidate.id === a.taskId);
    try {
      await this.saveOrKeep({ a: {
        ...a,
        ...(exercise?.exposureId ? { exposureId: exercise.exposureId } : {}),
        ...(!a.revealed && !a.skipped && !a.selfReport ? { interaction: 'first_attempt' as const } : {}),
        attemptId: makeClientId(), ...cardMs(this.momentId, a.taskId),
      }, label });
    } finally {
      this.saving = false;
    }
  }

  /** A failed save is kept for Retry rather than lost silently; a refused one is said, not retried. */
  private async saveOrKeep(u: UnsavedAttempt) {
    // A save during a Retry joins it, so the answers still land oldest first.
    if (this.retrying) {
      this.unsaved = [...this.unsaved, u];
      return;
    }
    const r = await this.send(u);
    if (r === 'retry') this.unsaved = [...this.unsaved, u];
    else if (r === 'refused') this.lost += 1;
  }

  /** An item's word as the app names it (Finglish first for Persian), from the moment's analysis; else its key. */
  private wordOf(key: string): string {
    const a = this.moment?.analysis;
    const all = [...(a?.vocabulary ?? []), ...(a?.expressions ?? []), ...(a?.suggested_replies ?? [])];
    const hit = all.find((x) => normalise(x.ca) === key);
    return hit ? hit.latin || hit.ca : key;
  }

  /** Send one answer to the learner's record. */
  private send(u: UnsavedAttempt): Promise<SendResult> {
    let operation: Promise<SendResult>;
    operation = this.sendOne(u).finally(() => this.sendOperations.delete(operation));
    this.sendOperations.add(operation);
    return operation;
  }

  private async sendOne(u: UnsavedAttempt): Promise<SendResult> {
    try {
      const request = api.recordAttempt(this.momentId, u.a);
      const p = bounded(request, ANSWER_TIMEOUT_MS, 'Answer save');
      this.inflight.add(p);
      const r = await p.finally(() => this.inflight.delete(p));
      this.savedRunTasks.add(u.a.taskId);
      routeOutcome(this.routeAttempt, this.routeTask, 'saved');
      if (u.a.skipped) this.skippedRunTasks.add(u.a.taskId);
      if (u.a.selfReport || u.a.productionKind === 'self_report') this.selfReportedRunTasks.add(u.a.taskId);
      if (u.a.revealed) this.revealedRunTasks.add(u.a.taskId);
      if ((u.a.hintsUsed ?? 0) > 0) this.hintedRunTasks.add(u.a.taskId);
      if (u.a.exposureId) void api.exerciseEvent(this.momentId, u.a.exposureId, makeClientId(), 'browser_ack').catch(() => undefined);
      if (r.leftFog) feel('fog');
      this.tally = tallyAnswer(this.tally, { ...u.a, graded: r.graded }, u.a.itemKey, this.wordOf(u.a.itemKey), r);
      if (r.becameForged && u.label) {
        this.forgedNow = [...this.forgedNow, u.label];
      }
      if (r.retreat) this.queueRetreat(withoutSaying(r.retreat).task);
      return 'saved';
    } catch (e) {
      console.warn('could not record attempt', e);
      if (e instanceof HttpError && e.code === 'attempt-item-removed') {
        this.attemptError = errorText(e);
        return 'item-removed';
      }
      return isRetryable(e) ? 'retry' : 'refused';
    }
  }

  /** Re-send the unsaved answers oldest first, same ids; stop at the first that still fails. */
  private retryUnsaved = async () => {
    if (this.retrying || this.ending) return;
    this.retrying = true;
    while (this.unsaved.length) {
      const u = this.unsaved[0];
      const r = await this.send(u);
      if (r === 'retry') break;
      this.unsaved = this.unsaved.filter((x) => x !== u);
      if (r === 'refused') this.lost += 1;
    }
    this.retrying = false;
    if (this.done) void this.refreshProgress();
  };

  private toggleSkipMenu = () => {
    this.skipMenu = !this.skipMenu;
  };

  private skipWithReason = (reason: SkipReason) => {
    if (this.mode !== 'test' || this.done || this.ending) return;
    const task = this.tasks[this.i];
    if (!task) return;
    if (this.recordedTasks.has(task.id)) {
      // The skip already saved (a double-tap while it was in flight):
      // just move on.
      this.skipMenu = false;
      this.advance();
      return;
    }
    this.testSeenTasks.add(task.id);
    this.clearAutoAdvance();
    this.skipMenu = false;
    // Start the save first, then advance: starting recordAt registers its
    // send synchronously, so the last card's finish still waits for it —
    // while the next card renders at once and Skip stays tappable during
    // the save (base behavior: Skip was never disabled). Record the task id
    // before advancing so a double-tap on the same card records once.
    this.recordedTasks.add(task.id);
    const at = this.i;
    const pending = this.recordAt(at, {
      taskId: task.id, itemKey: task.itemKey, type: task.type, correct: false,
      skipped: true, skipReason: reason, production: task.production, productionKind: task.productionKind,
    });
    this.advance();
    // Temporary skips are unscored and leave scheduling untouched.
    void pending;
  };

  /**
   * Remove a card from future lessons after explicit confirmation. Only advances once the status update succeeds — on
   * failure the card stays open with an error. The task is spliced out of
   * this run so an "Again" restart can't bring it back either.
   */
  private removeCurrentCard = async () => {
    if (this.mode !== 'test' || this.done || this.ending) return;
    const task = this.tasks[this.i];
    if (!task) return;
    this.testSeenTasks.add(task.id);
    const label = String((task.props as Record<string, unknown>).ca ?? (task.props as Record<string, unknown>).sentence ?? task.itemKey).slice(0, 60);
    if (!confirm(t('lesson.removeConfirm', { label: `${FSI}${label}${PDI}` }))) return;
    this.skipMenu = false;
    this.actionError = '';
    this.saving = true;
    const operationScope = `${this.momentId}:${task.id}`;
    const operationId = this.pendingRemovalOperations.get(operationScope) ?? makeClientId();
    this.pendingRemovalOperations.set(operationScope, operationId);
    try {
      const { previousStatus, statusVersion } = await api.setItemStatus(this.momentId, task.itemKey, 'removed', { operationId });
      const at = this.i;
      const removedIndexes = new Set<number>();
      const queuedTasks = this.tasks.flatMap((queuedTask, index) => {
        if (queuedTask.itemKey !== task.itemKey) return [];
        removedIndexes.add(index);
        return [{ task: queuedTask, index, misses: this.misses.filter((miss) => miss.i === index) }];
      });
      this.removedTasks = [...this.removedTasks, {
        itemKey: task.itemKey,
        tasks: queuedTasks,
        answered: this.answered,
        showMe: this.showMe,
        previousStatus: previousStatus ?? 'active',
        statusVersion: statusVersion ?? 0,
      }];
      this.pendingRemovalOperations.delete(operationScope);
      // A previously visited duplicate must not return when Again resets this
      // run's cursor and answer tracking. All copies are snapshotted so Undo
      // still restores the original pack order in this open run.
      this.tasks = this.tasks.filter((queuedTask) => queuedTask.itemKey !== task.itemKey);
      this.i = at - [...removedIndexes].filter((index) => index < at).length;
      this.misses = this.misses
        .filter((miss) => !removedIndexes.has(miss.i))
        .map((miss) => ({ ...miss, i: miss.i - [...removedIndexes].filter((index) => index < miss.i).length }));
    } catch (err) {
      if (isItemStatusConflict(err)) this.pendingRemovalOperations.delete(operationScope);
      this.actionError = isItemStatusConflict(err)
        ? t('lesson.statusConflict')
        : t('lesson.removeFailed', { err: errorText(err) });
      return;
    } finally {
      this.saving = false;
    }
    // Stay on the same index: it now holds the next card (or finishes).
    if (this.i >= this.tasks.length) {
      void this.finishCompleted();
      return;
    }
    this.answered = false;
    this.showMe = false;
    this.resetPron();
    this.renderTask();
  };

  private undoRemoval = async () => {
    let removed = this.removedTasks.at(-1);
    if (!removed?.tasks.length) return;
    const currentIndex = this.i;
    const currentTaskId = this.tasks[currentIndex]?.id;
    const currentAnswered = this.answered;
    const currentShowMe = this.showMe;
    this.actionError = '';
    this.saving = true;
    if (!removed.undoOperationId) {
      removed = { ...removed, undoOperationId: makeClientId() };
      this.removedTasks = [...this.removedTasks.slice(0, -1), removed];
    }
    try {
      await api.setItemStatus(this.momentId, removed.itemKey, removed.previousStatus, {
        expectedVersion: removed.statusVersion, operationId: removed.undoOperationId,
      });
      this.removedTasks = this.removedTasks.slice(0, -1);
      // Judged now, not when Undo was tapped: a completion can finish while
      // the POST is pending, and its done=true would hide the restored card.
      const waitingAtEnd = this.done;
      const restoredTasks = [...this.tasks];
      let restoredMisses = [...this.misses];
      let firstRestoredAt: number | undefined;
      for (const snapshot of removed.tasks) {
        const at = Math.max(0, Math.min(snapshot.index, restoredTasks.length));
        firstRestoredAt ??= at;
        restoredTasks.splice(at, 0, snapshot.task);
        restoredMisses = [
          ...restoredMisses.map((miss) => (miss.i >= at ? { ...miss, i: miss.i + 1 } : miss)),
          ...snapshot.misses.map((miss) => ({ ...miss, i: at })),
        ];
      }
      this.tasks = restoredTasks;
      this.misses = restoredMisses;
      // A completion still in flight (removal emptied the run) must stand
      // down: its done=true would hide the card just restored (ticket 92).
      this.finishSeq++;
      // Keep the current card in place when an earlier removed card is restored.
      // If the removed card was the final card, Undo from the end screen reopens it.
      const restoredCurrentIndex = currentTaskId ? this.tasks.findIndex((task) => task.id === currentTaskId) : -1;
      this.i = waitingAtEnd ? firstRestoredAt! : restoredCurrentIndex >= 0 ? restoredCurrentIndex : currentIndex;
      if (waitingAtEnd) this.done = false;
      this.answered = waitingAtEnd ? removed.answered : currentAnswered;
      this.showMe = waitingAtEnd ? removed.showMe : currentShowMe;
      this.skipMenu = false;
      this.error = '';
      this.resetPron();
      this.renderTask();
    } catch (err) {
      if (isItemStatusConflict(err)) {
        this.removedTasks = this.removedTasks.slice(0, -1);
        this.actionError = t('lesson.undoConflict');
      } else {
        this.actionError = t('lesson.undoFailed', { err: errorText(err) });
      }
    } finally { this.saving = false; }
  };

  private next = () => {
    if (this.mode === 'test' && (!this.answered || this.showMe)) return; // hard gate
    this.advance();
  };

  private advance() {
    this.clearAutoAdvance();
    if (this.i + 1 >= this.tasks.length) {
      void this.finishCompleted();
      return;
    }
    this.i += 1;
    this.answered = false;
    this.showMe = false;
    this.skipMenu = false;
    this.actionError = '';
    this.resetPron();
    this.renderTask();
  }

  private async refreshProgress() {
    this.progressUnavailable = true;
    try {
      this.progress = await bounded(api.progress(this.momentId), PROGRESS_TIMEOUT_MS, 'Progress refresh');
      this.progressUnavailable = false;
    } catch (e) {
      console.warn('could not load progress', e);
    }
  }

  /** Finish only after every answer and retry already in flight has settled. */
  private async waitForSends() {
    while (this.sendOperations.size || this.retrying) {
      const pending = [...this.sendOperations];
      if (pending.length) await Promise.allSettled(pending);
      else await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }

  /** The last card was reached; wait for authoritative saves before showing completion. */
  private async finishCompleted() {
    if (this.done || this.ending) return;
    this.ending = true;
    const mine = this.finishSeq;
    // Undo during the waits restores cards: stand down, so the run stays
    // open on the restored card instead of finishing over it.
    await this.waitForSends();
    if (mine !== this.finishSeq) { this.ending = false; return; }
    await this.refreshProgress();
    if (mine !== this.finishSeq) { this.ending = false; return; }
    if (!this.isConnected) return;
    this.endedEarly = false;
    this.done = true;
    this.ending = false;
  }
}
